// ═══════════════════════════════════════════════════════════════
// ADAPTADOR PÚBLICO — radioContent.ts
// Lee del motor canónico contentMaster.ts y exporta en el formato
// legacy esperado por las páginas públicas:
//   /photos, /videos, /radio/:freq (feed, fotos, videos, lives)
// ═══════════════════════════════════════════════════════════════

import {
  canonicalPosts,
  canonicalPhotos,
  canonicalVideos,
  canonicalLives,
  canonicalExternalPhotos,
  getPhotoUrl,
  type CanonicalPost,
  type CanonicalPhoto,
  type CanonicalVideo,
  type CanonicalLive,
} from './contentMaster';
import { radioEventsExtended } from './radioEvents';

// ─── Tipos Legacy (sin cambios, compatibles con todas las páginas) ───

export interface RadioPost {
  id: number;
  stationId: number;
  date: string;
  text: string;
  imageUrl?: string;
  videoUrl?: string;
  likes: number;
  comments: number;
  shares: number;
  liked: boolean;
  type: 'texto' | 'imagen' | 'video' | 'evento' | 'live' | 'concurso' | 'anuncio';
  featured: boolean;
  eventDate?: string;
  eventTime?: string;
  eventCity?: string;
}

export type PhotoCategory = 'cabina' | 'eventos' | 'locutores' | 'conciertos' | 'comunidad' | 'promociones';

export const PHOTO_CATEGORIES: { key: PhotoCategory; label: string; icon: string }[] = [
  { key: 'cabina', label: 'Cabina', icon: 'ri-mic-line' },
  { key: 'eventos', label: 'Eventos', icon: 'ri-calendar-event-line' },
  { key: 'locutores', label: 'Locutores', icon: 'ri-user-star-line' },
  { key: 'conciertos', label: 'Conciertos', icon: 'ri-music-2-line' },
  { key: 'comunidad', label: 'Comunidad', icon: 'ri-group-line' },
  { key: 'promociones', label: 'Promociones', icon: 'ri-gift-line' },
];

export interface RadioPhoto {
  id: number;
  stationId: number;
  url: string;
  caption: string;
  date: string;
  likes: number;
  liked: boolean;
  category: PhotoCategory;
}

export interface RadioVideo {
  id: number;
  stationId: number;
  thumbnailUrl: string;
  title: string;
  duration: string;
  date: string;
  views: number;
  category: VideoCategory;
}

export type VideoCategory = 'programas' | 'entrevistas' | 'conciertos' | 'shorts' | 'promociones';

export const VIDEO_CATEGORIES: { key: VideoCategory; label: string; icon: string }[] = [
  { key: 'programas', label: 'Programas', icon: 'ri-tv-line' },
  { key: 'entrevistas', label: 'Entrevistas', icon: 'ri-chat-3-line' },
  { key: 'conciertos', label: 'Conciertos', icon: 'ri-music-2-line' },
  { key: 'shorts', label: 'Shorts', icon: 'ri-video-line' },
  { key: 'promociones', label: 'Promociones', icon: 'ri-gift-line' },
];

export interface RadioEvent {
  id: number;
  stationId: number;
  title: string;
  date: string;
  time: string;
  city: string;
  location: string;
  description: string;
  imageUrl: string;
  status: 'proximamente' | 'hoy' | 'finalizado';
}

export type LiveStatus = 'en_vivo' | 'programado' | 'finalizado';

export interface LiveComment {
  id: number;
  userName: string;
  avatarUrl: string;
  text: string;
  time: string;
}

export interface LiveReaction {
  type: string;
  count: number;
}

export interface RadioLive {
  id: number;
  stationId: number;
  title: string;
  description: string;
  hostName: string;
  imageUrl: string;
  date: string;
  duration: string;
  status: LiveStatus;
  viewers: number;
  reactions: LiveReaction[];
  comments: LiveComment[];
  scheduledDate?: string;
  scheduledTime?: string;
  // Live → Replay lineage (del motor Admin Radio)
  generatedRecordingId?: string;
  generatedVideoId?: string;
  generatedPostId?: string;
}

export interface RadioContactInfo {
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  website: string;
  facebook: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  x: string;
  telegram: string;
}

// ─── Mapping helpers ───────────────────────────────────────

function toLegacyId(canonicalId: string): number {
  return parseInt(canonicalId.split('-')[1], 10);
}

function extractNum(id: string): number {
  const match = id.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

// ─── Station assignment ────────────────────────────────────

const ALL_STATION_IDS = [1, 3, 5, 7, 1, 3, 5, 7, 1, 2, 3, 5, 1, 3, 5, 7, 1, 3, 5, 7, 1, 2, 3, 5];

function assignStation(index: number): number {
  return ALL_STATION_IDS[index % ALL_STATION_IDS.length];
}

// ─── Category mapping ──────────────────────────────────────

function mapPhotoCategory(cat: CanonicalPhoto['category']): PhotoCategory {
  if (cat === 'cabina') return 'cabina';
  if (cat === 'eventos') return 'conciertos';
  if (cat === 'locutores') return 'locutores';
  if (cat === 'comunidad') return 'comunidad';
  if (cat === 'promociones') return 'promociones';
  return 'cabina';
}

function mapVideoCategory(cat: CanonicalVideo['category']): VideoCategory {
  if (cat === 'programas') return 'programas';
  if (cat === 'entrevistas') return 'entrevistas';
  if (cat === 'eventos') return 'conciertos';
  if (cat === 'shorts') return 'shorts';
  if (cat === 'promociones') return 'promociones';
  return 'programas';
}

function mapPostType(pt: CanonicalPost['postType']): RadioPost['type'] {
  if (pt === 'text') return 'texto';
  if (pt === 'image') return 'imagen';
  if (pt === 'video') return 'video';
  if (pt === 'poll') return 'texto';
  if (pt === 'sponsored') return 'anuncio';
  return 'texto';
}

function mapLiveStatus(s: CanonicalLive['status']): LiveStatus {
  if (s === 'live') return 'en_vivo';
  if (s === 'scheduled') return 'programado';
  return 'finalizado';
}

function computeDuration(l: CanonicalLive): string {
  if (l.status === 'live') return 'En curso · 2h 34min';
  if (l.status === 'scheduled') return '2h 00min';
  return '1h 45min';
}

// ─── Mock engagement generators ────────────────────────────

const LIVE_AVATARS = [
  'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20friendly%20person%20with%20warm%20smile%2C%20natural%20lighting%2C%20simple%20clean%20background%2C%20casual%20portrait%20photography&width=80&height=80&seq=avatar-live-a1&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20person%20with%20friendly%20expression%2C%20outdoor%20natural%20light%2C%20casual%20style%2C%20simple%20background%20portrait&width=80&height=80&seq=avatar-live-b2&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20young%20person%20with%20bright%20smile%2C%20studio%20lighting%2C%20clean%20white%20background%2C%20professional%20headshot%20style%20portrait&width=80&height=80&seq=avatar-live-c3&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20person%20with%20warm%20greeting%20and%20casual%20style%2C%20natural%20lighting%2C%20relaxed%20friendly%20portrait%20photography&width=80&height=80&seq=avatar-live-d4&orientation=squarish',
  'https://readdy.ai/api/search-image?query=Profile%20photo%20of%20person%20with%20curly%20hair%20smiling%20warmly%2C%20soft%20natural%20light%2C%20clean%20background%2C%20genuine%20candid%20portrait&width=80&height=80&seq=avatar-live-e5&orientation=squarish',
];

const LIVE_NAMES = ['María García', 'José Luis', 'Ana Martínez', 'Pedro Ramírez', 'Laura Díaz'];
const LIVE_TEXTS = [
  '¡Qué buena transmisión! Saludos desde la comunidad 🎵',
  'Están sonando increíble hoy 🙌',
  'La mejor radio sin duda alguna 🔥',
  'Saludos a todo el equipo, los escucho todos los días',
  'Estoy sintonizando desde el trabajo, me alegran el día 💕',
];

function mockViewers(status: LiveStatus): number {
  if (status === 'en_vivo') return 500 + Math.floor(Math.random() * 1500);
  if (status === 'finalizado') return 2000 + Math.floor(Math.random() * 15000);
  return 0;
}

function mockReactions(): LiveReaction[] {
  return [
    { type: '❤️', count: 200 + Math.floor(Math.random() * 500) },
    { type: '👍', count: 150 + Math.floor(Math.random() * 400) },
    { type: '🔥', count: 80 + Math.floor(Math.random() * 250) },
    { type: '👏', count: 60 + Math.floor(Math.random() * 200) },
  ];
}

function mockComments(status: LiveStatus): LiveComment[] {
  if (status === 'programado') return [];
  const count = status === 'en_vivo' ? 4 : 2;
  const result: LiveComment[] = [];
  for (let i = 0; i < count; i++) {
    result.push({
      id: i + 1,
      userName: LIVE_NAMES[i],
      avatarUrl: LIVE_AVATARS[i],
      text: LIVE_TEXTS[i],
      time: status === 'en_vivo' ? 'Hace ' + (i + 1) + ' min' : 'Finalizado',
    });
  }
  return result;
}

// ─── ADAPTADORES ───────────────────────────────────────────

function adaptPhoto(p: CanonicalPhoto, index: number): RadioPhoto {
  const dateOnly = p.createdAt.split('T')[0];
  return {
    id: toLegacyId(p.id),
    stationId: assignStation(index),
    url: p.imageUrl,
    caption: p.title,
    date: dateOnly,
    likes: 100 + Math.floor(Math.random() * 800),
    liked: false,
    category: mapPhotoCategory(p.category),
  };
}

function adaptExternalPhoto(ep: { id: string; imageUrl: string }, index: number): RadioPhoto {
  return {
    id: 100 + toLegacyId(ep.id),
    stationId: 1,
    url: ep.imageUrl,
    caption: 'Contenido destacado de la radio',
    date: '2026-06-15',
    likes: 200 + Math.floor(Math.random() * 600),
    liked: false,
    category: 'promociones' as PhotoCategory,
  };
}

function adaptVideo(v: CanonicalVideo, index: number): RadioVideo {
  const dateOnly = v.createdAt.split('T')[0];
  return {
    id: toLegacyId(v.id),
    stationId: assignStation(index),
    thumbnailUrl: getPhotoUrl(v.thumbnailPhotoId),
    title: v.title,
    duration: v.duration,
    date: dateOnly,
    views: 1000 + Math.floor(Math.random() * 15000),
    category: mapVideoCategory(v.category),
  };
}

function adaptPost(p: CanonicalPost, index: number): RadioPost {
  const base: RadioPost = {
    id: toLegacyId(p.id),
    stationId: assignStation(index),
    date: p.createdAt,
    text: p.text,
    likes: p.engagement.likes,
    comments: p.engagement.comments,
    shares: p.engagement.shares,
    liked: false,
    type: mapPostType(p.postType),
    featured: p.isFeatured,
  };

  if (p.imagePhotoId) {
    base.imageUrl = getPhotoUrl(p.imagePhotoId);
  }
  if (p.videoId) {
    const vid = canonicalVideos.find(function(v) { return v.id === p.videoId; });
    if (vid) base.videoUrl = getPhotoUrl(vid.thumbnailPhotoId);
  }
  if (p.referencesEventId) {
    base.type = 'evento';
  }

  return base;
}

function adaptLive(l: CanonicalLive, index: number): RadioLive {
  const status = mapLiveStatus(l.status);
  const dateOnly = l.date;
  return {
    id: extractNum(l.id),
    stationId: assignStation(index),
    title: l.title,
    description: l.description,
    hostName: l.hostDisplayName,
    imageUrl: getPhotoUrl(l.thumbnailPhotoId),
    date: dateOnly,
    duration: computeDuration(l),
    status: status,
    viewers: mockViewers(status),
    reactions: status === 'programado' ? [] : mockReactions(),
    comments: mockComments(status),
    scheduledDate: status === 'programado' ? dateOnly : undefined,
    scheduledTime: status === 'programado' ? l.time : undefined,
    generatedRecordingId: l.generatedRecordingId || undefined,
    generatedVideoId: l.generatedVideoId || undefined,
    generatedPostId: l.generatedPostId || undefined,
  };
}

// ─── BUILD DATA ────────────────────────────────────────────

export const radioPhotos: RadioPhoto[] = [
  ...canonicalPhotos.map(function(p, i) { return adaptPhoto(p, i); }),
  ...canonicalExternalPhotos.map(function(ep, i) { return adaptExternalPhoto(ep, i); }),
];

export const radioVideos: RadioVideo[] = canonicalVideos
  .filter(function(v) { return v.status === 'published'; })
  .map(function(v, i) { return adaptVideo(v, i); });

export const radioPosts: RadioPost[] = canonicalPosts
  .filter(function(p) { return p.status === 'published'; })
  .map(function(p, i) { return adaptPost(p, i); });

export const radioLives: RadioLive[] = canonicalLives
  .filter(function(l) { return l.status !== 'draft' && l.status !== 'archived'; })
  .map(function(l, i) { return adaptLive(l, i); })
  .sort(function(a, b) {
    const order: Record<LiveStatus, number> = { en_vivo: 0, programado: 1, finalizado: 2 };
    return order[a.status] - order[b.status];
  });

export const radioEvents: RadioEvent[] = radioEventsExtended;

// ─── ACCESSORS ─────────────────────────────────────────────

export function getStationPosts(stationId: number): RadioPost[] {
  return radioPosts
    .filter(function(p) { return p.stationId === stationId; })
    .sort(function(a, b) { return new Date(b.date).getTime() - new Date(a.date).getTime(); });
}

export function getStationPhotos(stationId: number): RadioPhoto[] {
  return radioPhotos.filter(function(p) { return p.stationId === stationId; });
}

export function getStationVideos(stationId: number): RadioVideo[] {
  return radioVideos
    .filter(function(v) { return v.stationId === stationId; })
    .sort(function(a, b) { return new Date(b.date).getTime() - new Date(a.date).getTime(); });
}

export function getStationLives(stationId: number): RadioLive[] {
  return radioLives
    .filter(function(l) { return l.stationId === stationId; })
    .sort(function(a, b) {
      const order: Record<LiveStatus, number> = { en_vivo: 0, programado: 1, finalizado: 2 };
      const statusDiff = order[a.status] - order[b.status];
      if (statusDiff !== 0) return statusDiff;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
}

export function getStationEvents(stationId: number): RadioEvent[] {
  return radioEvents
    .filter(function(e) { return e.stationId === stationId; })
    .sort(function(a, b) {
      const order: Record<string, number> = { hoy: 0, proximamente: 1, finalizado: 2 };
      const statusDiff = order[a.status] - order[b.status];
      if (statusDiff !== 0) return statusDiff;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
}

export function getStationSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ─── Contacts ──────────────────────────────────────────────

export const radioContacts: Record<number, RadioContactInfo> = {
  1: {
    phone: '+1 (707) 555-0142', whatsapp: '+1 (707) 555-0142',
    email: 'contacto@radionuevaactitud.com', address: '1200 Main Street, Santa Rosa, CA 95401, Estados Unidos',
    facebook: 'facebook.com/radionuevaactitud', instagram: '@radionuevaactitud',
    tiktok: '@radionuevaactitud', youtube: 'youtube.com/@radionuevaactitud',
    x: '@nuevaactitudfm', telegram: 't.me/radionuevaactitud', website: 'radionuevaactitud.com',
  },
  2: {
    phone: '+1 (305) 555-0234', whatsapp: '+1 (305) 555-0234',
    email: 'contacto@radiofeyesperanza.com', address: '456 Coral Way, Miami, FL 33145, Estados Unidos',
    facebook: 'facebook.com/radiofeyesperanza', instagram: '@radiofeyesperanza',
    tiktok: '@radiofeyesperanza', youtube: 'youtube.com/@radiofeyesperanza',
    x: '@feyesperanzafm', telegram: 't.me/radiofeyesperanza', website: 'radiofeyesperanza.com',
  },
  3: {
    phone: '+1 (713) 555-0456', whatsapp: '+1 (713) 555-0456',
    email: 'contacto@lamexicanafm.com', address: '789 Hillcroft Avenue, Houston, TX 77036, Estados Unidos',
    facebook: 'facebook.com/lamexicanafm', instagram: '@lamexicanafm',
    tiktok: '@lamexicanafm', youtube: 'youtube.com/@lamexicanafm',
    x: '@lamexicanafm', telegram: 't.me/lamexicanafm', website: 'lamexicanafm.com',
  },
  4: {
    phone: '+1 (212) 555-0678', whatsapp: '+1 (212) 555-0678',
    email: 'contacto@noticias247radio.com', address: '350 Fifth Avenue, Suite 1800, New York, NY 10118, Estados Unidos',
    facebook: 'facebook.com/noticias247radio', instagram: '@noticias247radio',
    tiktok: '@noticias247radio', youtube: 'youtube.com/@noticias247radio',
    x: '@noticias247', telegram: 't.me/noticias247radio', website: 'noticias247radio.com',
  },
  5: {
    phone: '+1 (310) 555-0890', whatsapp: '+1 (310) 555-0890',
    email: 'contacto@rocktotalradio.com', address: '2200 Sunset Boulevard, Los Angeles, CA 90046, Estados Unidos',
    facebook: 'facebook.com/rocktotalradio', instagram: '@rocktotalradio',
    tiktok: '@rocktotalradio', youtube: 'youtube.com/@rocktotalradio',
    x: '@rocktotalradio', telegram: 't.me/rocktotalradio', website: 'rocktotalradio.com',
  },
  6: {
    phone: '+1 (312) 555-0123', whatsapp: '+1 (312) 555-0123',
    email: 'contacto@radiocomunitariaunida.com', address: '100 W Randolph Street, Chicago, IL 60601, Estados Unidos',
    facebook: 'facebook.com/radiocomunitariaunida', instagram: '@radiocomunitariaunida',
    tiktok: '@radiocomunitariaunida', youtube: 'youtube.com/@radiocomunitariaunida',
    x: '@comunitariaunida', telegram: 't.me/radiocomunitariaunida', website: 'radiocomunitariaunida.com',
  },
  7: {
    phone: '+1 (214) 555-0345', whatsapp: '+1 (214) 555-0345',
    email: 'contacto@deportestotalradio.com', address: '2500 Victory Avenue, Dallas, TX 75219, Estados Unidos',
    facebook: 'facebook.com/deportestotalradio', instagram: '@deportestotalradio',
    tiktok: '@deportestotalradio', youtube: 'youtube.com/@deportestotalradio',
    x: '@deportestotal', telegram: 't.me/deportestotalradio', website: 'deportestotalradio.com',
  },
  8: {
    phone: '+1 (415) 555-0567', whatsapp: '+1 (415) 555-0567',
    email: 'contacto@radiointernacionalvoz.com', address: '1 Market Street, San Francisco, CA 94105, Estados Unidos',
    facebook: 'facebook.com/radiointernacionalvoz', instagram: '@radiointernacionalvoz',
    tiktok: '@radiointernacionalvoz', youtube: 'youtube.com/@radiointernacionalvoz',
    x: '@internacionalvoz', telegram: 't.me/radiointernacionalvoz', website: 'radiointernacionalvoz.com',
  },
  9: {
    phone: '+1 (602) 555-0789', whatsapp: '+1 (602) 555-0789',
    email: 'contacto@lakalle99.com', address: '2600 N Central Avenue, Phoenix, AZ 85004, Estados Unidos',
    facebook: 'facebook.com/lakalle99', instagram: '@lakalle99',
    tiktok: '@lakalle99', youtube: 'youtube.com/@lakalle99',
    x: '@lakalle99', telegram: 't.me/lakalle99', website: 'lakalle99.com',
  },
  10: {
    phone: '+1 (303) 555-0901', whatsapp: '+1 (303) 555-0901',
    email: 'contacto@radiovozdelpueblo.com', address: '1600 Broadway, Denver, CO 80202, Estados Unidos',
    facebook: 'facebook.com/radiovozdelpueblo', instagram: '@radiovozdelpueblo',
    tiktok: '@radiovozdelpueblo', youtube: 'youtube.com/@radiovozdelpueblo',
    x: '@vozdelpueblo', telegram: 't.me/radiovozdelpueblo', website: 'radiovozdelpueblo.com',
  },
  11: {
    phone: '+1 (407) 555-0123', whatsapp: '+1 (407) 555-0123',
    email: 'contacto@radiovidanueva.com', address: '8001 Orange Blossom Trail, Orlando, FL 32809, Estados Unidos',
    facebook: 'facebook.com/radiovidanueva', instagram: '@radiovidanueva',
    tiktok: '@radiovidanueva', youtube: 'youtube.com/@radiovidanueva',
    x: '@vidanuevafm', telegram: 't.me/radiovidanueva', website: 'radiovidanueva.com',
  },
  12: {
    phone: '+1 (210) 555-0456', whatsapp: '+1 (210) 555-0456',
    email: 'contacto@bandaradiomx.com', address: '300 Alamo Plaza, San Antonio, TX 78205, Estados Unidos',
    facebook: 'facebook.com/bandaradiomx', instagram: '@bandaradiomx',
    tiktok: '@bandaradiomx', youtube: 'youtube.com/@bandaradiomx',
    x: '@bandaradiomx', telegram: 't.me/bandaradiomx', website: 'bandaradiomx.com',
  },
};

export function getStationContact(stationId: number): RadioContactInfo {
  return radioContacts[stationId] || radioContacts[1];
}